import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/** Skeleton loading block. */
export function LoadingBlock({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn('space-y-2.5', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-3 animate-pulse rounded bg-surface-2"
          style={{ width: `${90 - i * 12}%` }}
        />
      ))}
    </div>
  )
}

/**
 * The three honest empty states — a user always knows which is which, and it is
 * never a blank panel or a fake zero.
 *   no-data  — nothing has been entered / computed yet
 *   degraded — computed-empty: engine metadata in progress (e.g. petrochem pack)
 *   error    — the fetch failed
 */
type EmptyKind = 'no-data' | 'degraded' | 'error'
const KIND: Record<EmptyKind, { color: string; label: string }> = {
  'no-data': { color: 'text-stable', label: 'No data yet' },
  degraded: { color: 'text-flare', label: 'Computed-empty · degraded' },
  error: { color: 'text-ignition', label: 'Error' },
}

export function EmptyState({
  kind,
  title,
  children,
  action,
}: {
  kind: EmptyKind
  title: string
  children?: ReactNode
  action?: ReactNode
}) {
  const k = KIND[kind]
  return (
    <div className="rounded-xl border border-dashed p-8 text-center">
      <div className="mx-auto max-w-sm">
        <div className="font-display font-semibold text-foreground">{title}</div>
        {children && <p className="mt-1.5 text-sm text-muted-foreground">{children}</p>}
        {action && <div className="mt-4 flex justify-center">{action}</div>}
        <div
          className={cn(
            'mt-4 font-display text-[10px] font-semibold uppercase tracking-wider',
            k.color,
          )}
        >
          {k.label}
        </div>
      </div>
    </div>
  )
}

export function ErrorState({ onRetry }: { onRetry?: () => void }) {
  return (
    <EmptyState
      kind="error"
      title="Couldn’t load this"
      action={
        onRetry ? (
          <button onClick={onRetry} className="text-sm font-semibold text-primary">
            Retry
          </button>
        ) : undefined
      }
    >
      Something went wrong fetching this data.
    </EmptyState>
  )
}
